import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchDashboardV2Financial,
  fetchDashboardV2Network,
  fetchDashboardV2Overview,
  fetchDashboardV2Roadblocks,
  fetchDashboardV2Shops,
  type AgentDashboardFinancialData,
  type AgentDashboardNetworkData,
  type AgentDashboardOverviewData,
  type AgentDashboardRoadblocksData,
  type AgentDashboardShopsData,
  type DashboardV2Period,
  type DashboardV2PeriodParams,
} from '../services/api';

type QueryState<T> = {
  data: T | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  refreshing: boolean;
  refetch: () => Promise<void>;
};

type Fetcher<T> = (
  period: DashboardV2Period,
  refresh?: boolean,
  extra?: Record<string, unknown>,
) => Promise<T>;

function useDashboardV2Query<T>(
  fetcher: Fetcher<T>,
  period: DashboardV2Period,
  extra?: Record<string, unknown>,
  enabled = true,
): QueryState<T> {
  const [data, setData] = useState<T | null>(null);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isFetching, setIsFetching] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const extraKey = JSON.stringify(extra ?? null);
  const requestIdRef = useRef(0);

  const load = useCallback(
    async (refresh = false, silent = false) => {
      if (!enabled) {
        setIsLoading(false);
        setIsFetching(false);
        setRefreshing(false);
        return;
      }
      const requestId = ++requestIdRef.current;
      if (refresh) setRefreshing(true);
      else if (!silent) setIsLoading(true);
      setIsFetching(true);
      setError(null);
      try {
        const parsedExtra = JSON.parse(extraKey) as Record<string, unknown> | null;
        const result = await fetcherRef.current(period, refresh, parsedExtra ?? undefined);
        if (requestId !== requestIdRef.current) return;
        setData(result);
      } catch (err) {
        if (requestId !== requestIdRef.current) return;
        const e = err instanceof Error ? err : new Error('Failed to load dashboard');
        setError(e);
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsFetching(false);
          setRefreshing(false);
        }
      }
    },
    [enabled, period, extraKey],
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  const refresh = useCallback(async () => load(true, true), [load]);
  const refetch = useCallback(async () => load(false, true), [load]);

  return { data, isLoading, isFetching, error, refresh, refreshing, refetch };
}

function rangeOpts(params: DashboardV2PeriodParams) {
  return {
    tz: params.tz,
    dateFrom: params.period === 'custom' ? params.dateFrom : undefined,
    dateTo: params.period === 'custom' ? params.dateTo : undefined,
  };
}

function isPeriodReady(params: DashboardV2PeriodParams): boolean {
  if (params.period !== 'custom') return true;
  return Boolean(params.dateFrom && params.dateTo);
}

export const useDashboardV2Overview = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<AgentDashboardOverviewData>(
    (p, refresh) => fetchDashboardV2Overview(p, refresh, rangeOpts(params)),
    params.period,
    rangeOpts(params),
    enabled && isPeriodReady(params),
  );

export const useDashboardV2Financial = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<AgentDashboardFinancialData>(
    (p, refresh) => fetchDashboardV2Financial(p, refresh, rangeOpts(params)),
    params.period,
    rangeOpts(params),
    enabled && isPeriodReady(params),
  );

export const useDashboardV2Shops = (
  params: DashboardV2PeriodParams,
  page: number,
  enabled = true,
) =>
  useDashboardV2Query<AgentDashboardShopsData>(
    (p, refresh) => fetchDashboardV2Shops(p, { page, ...rangeOpts(params) }, refresh),
    params.period,
    { page, ...rangeOpts(params) },
    enabled && isPeriodReady(params),
  );

export const useDashboardV2Network = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<AgentDashboardNetworkData>(
    (p, refresh) => fetchDashboardV2Network(p, refresh, rangeOpts(params)),
    params.period,
    rangeOpts(params),
    enabled && isPeriodReady(params),
  );

export const useDashboardV2Roadblocks = (params: DashboardV2PeriodParams, enabled = true) =>
  useDashboardV2Query<AgentDashboardRoadblocksData>(
    (p, refresh) => fetchDashboardV2Roadblocks(p, refresh, rangeOpts(params)),
    params.period,
    rangeOpts(params),
    enabled && isPeriodReady(params),
  );
